// 覚醒王の予備動作の声 (2026-10-13・音の専用セッション・記録 = docs/research/2026-10-13-boss-sound-identity.md §8)。
//   芯 = 戴冠獣と同じ喉を低く大きく＋怨念の声が重なる。見本「近い」= Bloodborne 血に飢えた獣 (10-13 ユーザー)。
//   素材は戴冠獣で採用した b 獅子寄りと同じテイクから作った (scripts/render_awoken_voice_sfx.py)。全ボス共通の唸りの代わりに鳴る。
//   既定 haunt (＋怨念の声・10-13 答えるページでユーザーが選んだ)。開発版 ?awokenVoice=deep (低く大きくだけ) / off (旧・共通の唸り)。
//   入りで見分けさせない組 (暴噛と暴噛・溜め／暴連噛と伸び／暴走突進と追い噛み) は同じ声にする (声で答えを漏らさない)。
import type { SfxId } from './types'
import type { HoundVoiceRole } from './hound_voice_sound'

export const AWOKEN_VOICE_TAKES = ['deep', 'haunt'] as const
export type AwokenVoiceTake = typeof AWOKEN_VOICE_TAKES[number]
export type AwokenVoice = 'off' | AwokenVoiceTake
const ROLES: readonly HoundVoiceRole[] = ['huff', 'growl_s', 'growl_l', 'bark', 'roar']
export const AWOKEN_VOICE_SFX_IDS = AWOKEN_VOICE_TAKES.flatMap(k => ROLES.map(r => `awoken_voice_${k}_${r}` as const))

export const AWOKEN_VOICE_DEFAULT: AwokenVoice = 'haunt'
let voice: AwokenVoice = AWOKEN_VOICE_DEFAULT
export function getAwokenVoice(): AwokenVoice { return voice }
export function setAwokenVoice(value: string | null | undefined): AwokenVoice {
  voice = value === 'deep' || value === 'off' ? value : AWOKEN_VOICE_DEFAULT
  if (voice !== 'off' && typeof fetch !== 'undefined') void import('./audio').then(m => m.preloadAwokenVoice(voice as AwokenVoiceTake))
  return voice
}

const TELL_ROLE: Readonly<Record<string, HoundVoiceRole>> = {
  feralWallDashBack: 'huff',
  feralBite: 'growl_s', feralBiteDelayed: 'growl_s', feralPocketSpin: 'growl_s',
  feralFrenzy: 'growl_l', feralFrenzyExtend: 'growl_l', feralRush: 'growl_l', feralRushChase: 'growl_l', feralRoar: 'growl_l',
  feralWallDash: 'bark', rampage: 'bark',
}
export function awokenVoiceRoleFor(attackId: string): HoundVoiceRole | null {
  if (attackId === 'rampageV5') return rampageVoice === 'bark' ? 'bark' : rampageVoice === 'roar' ? 'growl_l' : null
  return TELL_ROLE[attackId] ?? null
}

// 配布版の大暴れ (第5案・id rampageV5) は上の表に無く (表の rampage は ?rampage=old の旧版)、溜めで共通の唸りが鳴っている (10-17 確認)。
//   開発版 ?awokenRampageVoice= だけで比べる。既定 off = 今のまま (共通の唸り)。
//   bark = 表の決め「大暴れ＝吠え」どおり溜めの頭で吠える / roar = 軋む咆哮と同じ並び (溜めで長い唸り→雄叫びの絵 roar_up に替わる瞬間に咆哮)。
//   幕3の突入では軋む咆哮が大暴れに置き換わる (rampageV5EntryAttack) ので、今は咆哮の声もそこで消えている。
export type AwokenRampageVoice = 'off' | 'bark' | 'roar'
let rampageVoice: AwokenRampageVoice = 'off'
export function setAwokenRampageVoice(value: string | null | undefined): AwokenRampageVoice {
  rampageVoice = import.meta.env.DEV && (value === 'bark' || value === 'roar') ? value : 'off'
  return rampageVoice
}
/** 大暴れの雄叫びが一番高く上がった瞬間の声 (?awokenRampageVoice=roar の時だけ)。 */
export function awokenRampageRoarSfx(): SfxId | null {
  return voice === 'off' || rampageVoice !== 'roar' ? null : `awoken_voice_${voice}_roar` as SfxId
}
/** 溜めに入った時の声。null = 共通の唸りのまま。 */
export function awokenTellSfx(attackId: string): SfxId | null {
  const role = awokenVoiceRoleFor(attackId)
  return voice === 'off' || !role ? null : `awoken_voice_${voice}_${role}` as SfxId
}
/** 当たりに入った時の声 (軋む咆哮だけ)。 */
export function awokenActiveSfx(attackId: string): SfxId | null {
  return voice === 'off' || attackId !== 'feralRoar' ? null : `awoken_voice_${voice}_roar` as SfxId
}
export function awokenVoiceFiles(take: AwokenVoiceTake): [string, string][] {
  return ROLES.map(r => `awoken_voice_${take}_${r}`).map(id => [id, `/sfx/${id}.wav?v=1`])
}
